// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { findImprIdpDirectories } from '@api/commonServices';
import { Endpoints, StorageKeys } from '@api/constants';
import type { ImprDir } from '@api/types';
import { createCodingCtxV2 } from '@api/utils';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import type { AppError } from '@errorHandler/errors';
import { Button, InputBox } from '@imprivata-cloud/components';
import { Regex } from '@utils/utils';
import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContinueButton from '../../../components/continueButton/ContinueButton';
import ValidationRules, {
  type ValidationRule,
} from '../../../components/validationRules/ValidationRules';
import { STEPS } from '../../constants';
import { useAddImprIdpDirectory, useDirectoriesConfig } from '../../hooks/useImprivataIdp';
import { useNavigateToNextStep } from '../../hooks/useNavigateToNextStep';
import { useGetPasswordPolicy } from '@/self-service/hooks/useGetPasswordPolicy';

import './ImprivataIdp.less';
import { ErrorCode } from '@/errorHandler/constants';

const TRANSLATION_ROOT = `setup.${STEPS.IMPRIVATA_IDP}.content`;
const TRANSLATION_ERRORS = 'setup.external-idp-info.content.validation';

const DOMAIN_CONFLICT_CODES: readonly ErrorCode[] = [
  ErrorCode.DOMAIN_ALREADY_EXISTS,
  ErrorCode.DIFFERENT_CUSTOMER,
];

const INPUT_CLASSNAME = 'impr-idp-input';
const SUBTITLE_CLASSNAME = 'impr-idp-subtitle';

export interface ImprIdpFormValues {
  domainName: string;
  username: string;
  password: string;
  businessEmail: string;
}

interface PasswordPolicy {
  minimumLength: number;
  maximumLength: number;
}

const ImprivataIdp = () => {
  const navigateToNextStep = useNavigateToNextStep();
  const { mutateAsync: addIdpDirectory } = useAddImprIdpDirectory(
    Endpoints.SETUP_IMPR_IDP_DIR_ADD,
    DOMAIN_CONFLICT_CODES,
  );
  const [form] = Form.useForm<ImprIdpFormValues>();
  const { t } = useTranslation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [domainName, setDomainName] = useState<string>();
  const [domainServerError, setDomainServerError] = useState(false);
  const [businessEmail, setBusinessEmail] = useState(
    localStorage.getItem(StorageKeys.ORG_EMAIL) ?? '',
  );
  const [businessEmailServerError, setBusinessEmailServerError] = useState(false);
  const [usernameServerError, setUsernameServerError] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [focusedField, setFocusedField] = useState<
    'domainName' | 'businessEmail' | 'username' | 'password' | null
  >(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const [directory, setDirectory] = useState<ImprDir>();
  const lockedDomain = directory?.domains?.[0]?.domainName || undefined;
  const { emitError } = useErrorContext();
  const { data: directoriesConfig } = useDirectoriesConfig(true);
  const imprDomainSuffix = directoriesConfig?.imprDomainSuffix
    ? `.${directoriesConfig.imprDomainSuffix}`
    : '';
  const domainSuffix = lockedDomain?.replace(/^[^.]*/, '') ?? imprDomainSuffix;
  const [isLoading, setIsLoading] = useState(false);
  const [businessEmailError, setBusinessEmailError] = useState(false);
  const { data: policyData } = useGetPasswordPolicy(true);
  const passwordPolicy: PasswordPolicy = policyData
    ? {
        minimumLength: policyData.minimumLength.value,
        maximumLength: policyData.maximumLength.value,
      }
    : { minimumLength: 15, maximumLength: 256 };

  const handleSkip = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      navigateToNextStep(STEPS.EXTERNAL_IDP_INFO);
    },
    [navigateToNextStep],
  );

  function onSkipOrContinue() {
    navigateToNextStep(STEPS.EXTERNAL_IDP_INFO);
  }

  const domainRules: ValidationRule[] = [
    {
      id: 'length',
      label: value => `3 - 63 characters (${value.length}/63)`,
      validate: value => value.length >= 3 && value.length <= 63,
    },
    {
      id: 'characters',
      label: t(`${TRANSLATION_ROOT}.validation.domain`),
      validate: value => Regex.imprIdPDomain.test(value),
    },
    {
      id: 'hyphens',
      label: t(`${TRANSLATION_ROOT}.validation.domainHyphens`),
      validate: value => !value || (!value.startsWith('-') && !value.endsWith('-')),
    },
    {
      id: 'hyphenPosition',
      label: t(`${TRANSLATION_ROOT}.validation.domainHyphensPosition`),
      validate: value => value.length < 4 || Regex.imprIdPDomainHyphensPosition.test(value),
    },
  ];

  const businessEmailRules: ValidationRule[] = [
    {
      id: 'email',
      label: t(`${TRANSLATION_ERRORS}.enter-valid-email`),
      validate: value => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

        if (!emailRegex.test(value)) {
          return false;
        }

        const [localPart, domain] = value.split('@');

        // Local part cannot start/end with '.' or contain '..'
        if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
          return false;
        }

        // Domain validation
        if (
          domain.startsWith('.') ||
          domain.endsWith('.') ||
          domain.startsWith('-') ||
          domain.endsWith('-') ||
          domain.includes('..') ||
          domain.includes('--')
        ) {
          return false;
        }

        // Only allowed characters
        if (!/^[\p{L}\p{N}._+-]+@[\p{L}\p{N}.-]+$/u.test(value)) {
          return false;
        }
        // Block restricted Unicode scripts
        const restrictedUnicodeRegex =
          /[\p{Script=Han}\p{Script=Hangul}\p{Script=Arabic}\p{Script=Hiragana}\p{Script=Katakana}]/u;

        if (restrictedUnicodeRegex.test(value)) {
          return false;
        }

        return true;
      },
    },
  ];

  const usernameRules: ValidationRule[] = [
    {
      id: 'length',
      label: value => `6 - 64 characters (${value.length}/64)`,
      validate: value => value.length >= 6 && value.length <= 64,
    },
    {
      id: 'allowedCharacters',
      label: 'Only letters, numbers, ., _, and - are allowed',
      validate: value => /^[\p{L}\p{N}._-]+$/u.test(value),
    },
    {
      id: 'dotPosition',
      label: 'Cannot start or end with a dot',
      validate: value => !value.startsWith('.') && !value.endsWith('.'),
    },
    {
      id: 'consecutiveDots',
      label: 'Cannot contain consecutive dots',
      validate: value => !value.includes('..'),
    },
  ];

  const passwordRules: ValidationRule[] = [
    {
      id: 'min',
      label: `At least ${passwordPolicy.minimumLength} characters`,
      validate: value => value.length >= passwordPolicy.minimumLength,
    },
    {
      id: 'max',
      label: `Maximum ${passwordPolicy.maximumLength} characters`,
      validate: value => value.length <= passwordPolicy.maximumLength,
    },
  ];

  const isValid = (value: string, rules: ValidationRule[]) =>
    rules.every(rule => rule.validate(value));

  const hasError = (value: string, rules: ValidationRule[]) => !isValid(value, rules);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await findImprIdpDirectories(Endpoints.IMPR_IDP_DIR_FIND, true);
      if (response?.directories && response.directories.length > 0) {
        const directories = response.directories;
        const rawDomain = directories[0].domains?.[0]?.domainName;
        if (rawDomain) {
          const parsedDomain = rawDomain.split('.')[0];
          form.setFieldValue('domainName', parsedDomain);
          setDomainName(parsedDomain);
          setFocusedField(current => (current === 'domainName' ? null : current));
          setTouchedFields(current => {
            const next = new Set(current);
            next.delete('domainName');
            return next;
          });
          // Re-derive the sign-in domain on re-entry: sessionStorage does not survive
          // a browser session, so the completion page needs this rewritten here to show
          // directory guidance when the admin skips an already-configured step (ICP-2164).
          try {
            sessionStorage.setItem(StorageKeys.IMPR_IDP_DOMAIN, rawDomain);
          } catch {
            /* storage full — directory guidance omitted from the completion page */
          }
        }
        setDirectory(directories[0]);
        sessionStorage.setItem(StorageKeys.IMPR_IDP_USED, 'true');
      }
    } catch (error) {
      emitError(error as AppError);
    } finally {
      setIsLoading(false);
    }
  }, [form, emitError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const isBusinessEmailValid = (value: string) =>
    businessEmailRules.every(rule => rule.validate(value));

  const validateForm = () => {
    const values = form.getFieldsValue();

    const validations = [
      !lockedDomain && hasError(values.domainName ?? '', domainRules),
      hasError(values.username ?? '', usernameRules),
      hasError(values.password ?? '', passwordRules),
      hasError(businessEmail, businessEmailRules),
      domainServerError,
    ];

    return validations.some(Boolean);
  };

  const shouldShowError = (
    fieldName: keyof ImprIdpFormValues,
    value: string,
    rules: ValidationRule[],
  ) => focusedField !== fieldName && touchedFields.has(fieldName) && hasError(value, rules);

  const shouldShowValidation = (fieldName: keyof ImprIdpFormValues) => {
    return focusedField === fieldName;
  };

  const domainFormatError = shouldShowError('domainName', domainName ?? '', domainRules)
    ? t(`${TRANSLATION_ERRORS}.enter-valid-domain`)
    : undefined;

  const domainErrorMessage = domainServerError
    ? t('setup.errors.domain-already-exists')
    : domainFormatError;

  const getUsernameErrorMessage = () => {
    if (usernameServerError) {
      return t('setup.errors.already-exists');
    }
    if (shouldShowError('username', username, usernameRules)) {
      return t(`${TRANSLATION_ERRORS}.enter-valid-username`);
    }
    return undefined;
  };

  const handleFieldBlur = (fieldName: keyof ImprIdpFormValues) => {
    setFocusedField(null);
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  const handleChangeEmail = () => {
    setIsEditingEmail(true);
    form.setFieldValue('businessEmail', businessEmail);
  };

  const handleSaveEmail = async () => {
    try {
      await form.validateFields(['businessEmail']);
      setIsEditingEmail(false);
    } catch (e) {
      console.log('Error with business email: ', e);
    }
  };

  const onSubmit = async (values: ImprIdpFormValues) => {
    try {
      setIsLoading(true);
      setDomainServerError(false);
      const codingCtx = await createCodingCtxV2();
      const domainName =
        lockedDomain ||
        `${values?.domainName?.trim().toLowerCase().replaceAll('.', '-')}${imprDomainSuffix}`;
      const username = `${values?.username?.trim()}@${domainName}`;
      const password = codingCtx.encryptString(values?.password);

      const payload = {
        addDirectoryRequest: {
          directory: {
            domains: [{ domainName: domainName, isPrimary: true }],
            directoryId: directory?.directoryId,
          },
          initialUser: {
            username: username,
            password: password,
            ...(businessEmail && { email: businessEmail }),
          },
        },
        codingContext: codingCtx,
      };

      await addIdpDirectory(payload, {
        onSuccess: () => {
          console.log('Imprivata IdP Directory added successfully:');
          sessionStorage.setItem(StorageKeys.IMPR_IDP_USED, 'true');
          try {
            sessionStorage.setItem(StorageKeys.IMPR_IDP_USERNAME, username);
          } catch {
            /* storage full — username not stored */
          }
          try {
            sessionStorage.setItem(StorageKeys.IMPR_IDP_DOMAIN, domainName);
          } catch {
            /* storage full — domain not stored */
          }
          onSkipOrContinue();
        },
      });
    } catch (e) {
      const error = e as AppError;
      if (error.code === ErrorCode.ALREADY_EXISTS) {
        // Differentiate by error message
        if (error.message?.toLowerCase().includes('username')) {
          setUsername('');
          form.setFieldValue('username', '');
          setUsernameServerError(true);
        } else {
          setBusinessEmailServerError(true);
          setIsEditingEmail(true);
        }
        return;
      }
      if (error.code && DOMAIN_CONFLICT_CODES.includes(error.code)) {
        setDomainServerError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.subtitle`)}</div>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        aria-label={'impr-idp-form'}
        onFinish={onSubmit}
      >
        {/* DOMAIN */}
        <div className={INPUT_CLASSNAME}>
          <Form.Item
            name="domainName"
            validateStatus={domainErrorMessage ? 'error' : undefined}
            help={domainErrorMessage}
          >
            <InputBox
              autoFocus
              size="large"
              label="Domain"
              value={domainName}
              aria-label="domain"
              minLength={3}
              maxLength={63}
              disabled={!!lockedDomain}
              onFocus={() => setFocusedField('domainName')}
              onBlur={() => handleFieldBlur('domainName')}
              onChange={e => {
                const value = e.target.value.replaceAll('.', '-');
                setDomainName(value);
                setDomainServerError(false);
                form.setFieldValue('domainName', value);
              }}
            />
          </Form.Item>
          <span className="impr-idp-input-suffix">{domainSuffix}</span>
        </div>

        <div
          className={`validation-rules-container ${
            shouldShowValidation('domainName') ? 'is-visible' : ''
          }`}
        >
          <ValidationRules value={domainName ?? ''} rules={domainRules} />
        </div>

        <div style={{ height: '20px' }} />

        {/* BUSINESS EMAIL */}
        {isEditingEmail ? (
          <>
            <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.acct-label-prefix`)}</div>
            <div className={`${INPUT_CLASSNAME}-business-email`}>
              <Form.Item
                name="businessEmail"
                validateStatus={
                  businessEmailServerError || businessEmailError ? 'error' : undefined
                }
                help={t(`${TRANSLATION_ERRORS}.enter-valid-email`)}
              >
                <InputBox
                  autoFocus
                  value={businessEmail}
                  size="large"
                  onChange={e => {
                    const value = e.target.value;
                    setBusinessEmail(value);
                    setBusinessEmailServerError(false);
                    form.setFieldValue('businessEmail', value);
                    if (businessEmailError) {
                      setBusinessEmailError(!isBusinessEmailValid(value));
                    }
                  }}
                  onFocus={() => setFocusedField('businessEmail')}
                  onBlur={() => {
                    handleFieldBlur('businessEmail');
                    setBusinessEmailError(!isBusinessEmailValid(businessEmail));
                  }}
                />
              </Form.Item>
              <button
                type="button"
                className="change-email-button"
                onClick={handleSaveEmail}
                disabled={!isBusinessEmailValid(businessEmail)}
              >
                {t('common.save')}
              </button>
            </div>
          </>
        ) : (
          <div className={SUBTITLE_CLASSNAME}>
            {t(`${TRANSLATION_ROOT}.acct-label-prefix`)} <strong>{businessEmail}</strong>{' '}
            <button type="button" className="change-email-button" onClick={handleChangeEmail}>
              {t(`${TRANSLATION_ROOT}.acct-label-change`)}
            </button>
          </div>
        )}

        <div style={{ height: '20px' }} />

        {/* USERNAME */}
        <div className={INPUT_CLASSNAME}>
          <Form.Item
            name="username"
            validateStatus={
              shouldShowError('username', username, usernameRules) || usernameServerError
                ? 'error'
                : undefined
            }
            help={getUsernameErrorMessage()}
          >
            <InputBox
              size="large"
              aria-label={'username'}
              label={t('portal.appearance.username-label')}
              minLength={6}
              maxLength={64}
              value={username}
              onFocus={() => setFocusedField('username')}
              onBlur={() => handleFieldBlur('username')}
              onChange={e => {
                const value = e.target.value;
                setUsername(value);
                // Only clear server error if user is typing a new value (not empty)
                if (value) {
                  setUsernameServerError(false);
                }
                form.setFieldValue('username', value);
              }}
            />
          </Form.Item>
          <span className="impr-idp-input-suffix">
            {domainName ? `@${domainName}${domainSuffix}` : ''}
          </span>
        </div>
        <div
          className={`validation-rules-container ${
            shouldShowValidation('username') ? 'is-visible' : ''
          }`}
        >
          <ValidationRules value={username} rules={usernameRules} />
        </div>

        <div style={{ height: '20px' }} />

        {/* PASSWORD */}
        <div className={INPUT_CLASSNAME}>
          <Form.Item
            validateStatus={
              shouldShowError('password', password, passwordRules) ? 'error' : undefined
            }
            help={
              shouldShowError('password', password, passwordRules)
                ? t(`${TRANSLATION_ERRORS}.enter-valid-password`)
                : undefined
            }
            name="password"
            rules={[{ required: true }]}
          >
            <InputBox
              size="large"
              aria-label={'password'}
              label={t('portal.appearance.password-label')}
              minLength={passwordPolicy.minimumLength}
              maxLength={passwordPolicy.maximumLength}
              type="password"
              value={password}
              onFocus={() => setFocusedField('password')}
              onBlur={() => handleFieldBlur('password')}
              onChange={e => {
                setPassword(e.target.value);

                form.setFieldValue('password', e.target.value);
              }}
            />
          </Form.Item>
        </div>
        <div
          className={`validation-rules-container password-validation-rules ${
            shouldShowValidation('password') ? 'is-visible' : ''
          }`}
        >
          <ValidationRules value={password} rules={passwordRules} />
        </div>

        <div style={{ height: '20px' }} />

        <Form.Item className={'impr-idp-continue-button'}>
          {/* Continue */}
          <ContinueButton
            disabled={isLoading || isEditingEmail}
            label={'Continue'}
            htmlType="submit"
            overrideErrorCheck={() => validateForm()}
            validateDirty={false}
          />
        </Form.Item>
        <Button
          label={t('common.skip')}
          onClick={onSkipOrContinue}
          onMouseDown={handleSkip}
          size="large"
          className={'impr-idp-skip-button'}
        />
      </Form>
    </div>
  );
};

export default ImprivataIdp;
